import { useEffect, useState } from 'react'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Inbox,
  KeyRound,
  Play,
  Receipt,
  Save,
  Send,
  ShieldCheck,
  UserPlus,
  Users,
  ScrollText,
  XCircle,
  Zap,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input, Label, FieldHint, FieldError } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { CenterSpinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { useToast } from '@/components/ui/ToastProvider'
import { hu } from '@/lib/i18n/hu'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth/AuthProvider'
import { hasPermission } from '@/lib/permissions'
import {
  useAuditLogs,
  useCompanies,
  useCompanySettings,
  useCreateUser,
  useCronRuns,
  useCronStatus,
  useHealthIntegrations,
  useEmailAccount,
  useListImapFolders,
  useNavCredentials,
  useOutgoingEmail,
  useRunCronJob,
  useSyncSzamlazz,
  useSzamlazz,
  useTestEmailConnection,
  useTestNavConnection,
  useTestOutgoingEmail,
  useTestSzamlazz,
  useUpdateCompanySettings,
  useUpdateEmailAccount,
  useUpdateNavCredentials,
  useUpdateOutgoingEmail,
  useUpdateSzamlazz,
  useUpsertCompany,
  useUsers,
} from '@/lib/api/hooks'
import { dateTime } from '@/lib/format'
import type { Company, CompanySettings, OutgoingEmailSettings, Role, UUID } from '@/lib/api/types'

const NAV = [
  { to: 'overview', label: 'Áttekintés', icon: Activity },
  { to: 'companies', label: 'Cégek', icon: Building2 },
  { to: 'nav', label: 'NAV kapcsolat', icon: ShieldCheck },
  { to: 'szamlazz', label: 'Számlázz.hu (saját számlák)', icon: Receipt },
  { to: 'email', label: 'Bejövő email (IMAP)', icon: Inbox },
  { to: 'outgoing', label: 'Kimenő email (SMTP)', icon: Send },
  { to: 'accountant', label: 'Könyvelő', icon: Receipt },
  { to: 'automation', label: 'Automatizálás', icon: Zap },
  { to: 'users', label: 'Felhasználók', icon: Users },
  { to: 'audit', label: 'Audit log', icon: ScrollText },
]

export function SettingsPage() {
  return (
    <div>
      <PageHeader title={hu.nav.settings} subtitle="A modul üzemi paraméterei és integrációi" />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5">
        <aside className="lg:sticky lg:top-16 lg:self-start">
          <nav className="flex lg:flex-col gap-1 lg:gap-0.5 overflow-x-auto lg:overflow-visible whitespace-nowrap pb-1 lg:pb-0 -mx-3 lg:mx-0 px-3 lg:px-0 scrollbar-thin">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium flex-shrink-0',
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-200'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                  )
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <div>
          <Routes>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<OverviewSection />} />
            <Route path="companies" element={<CompaniesSection />} />
            <Route path="nav" element={<NavSection />} />
            <Route path="email" element={<EmailSection />} />
            <Route path="outgoing" element={<OutgoingEmailSection />} />
            <Route path="szamlazz" element={<SzamlazzSection />} />
            <Route path="accountant" element={<AccountantSection />} />
            <Route path="automation" element={<AutomationSection />} />
            <Route path="users" element={<UsersSection />} />
            <Route path="audit" element={<AuditSection />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

function CompaniesSection() {
  const { activeRole } = useAuth()
  const canManage = hasPermission(activeRole, 'settings.users')
  const { data, isLoading } = useCompanies()
  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Company | null>(null)

  function openNew() {
    setEditTarget(null)
    setModalOpen(true)
  }
  function openEdit(c: Company) {
    setEditTarget(c)
    setModalOpen(true)
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Cégek</CardTitle>
          <CardSubtitle>Tenant alatti céges entitások — minden cégnek külön NAV kapcsolat, könyvelő és export.</CardSubtitle>
        </div>
        {canManage && (
          <Button size="sm" onClick={openNew}>
            + Új cég
          </Button>
        )}
      </CardHeader>

      {isLoading ? (
        <CenterSpinner />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Nincs cég" description="Hozz létre egyet a + Új cég gombbal." />
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {data.map((c) => (
            <li key={c.id} className="flex items-center justify-between py-3 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="grid h-10 w-10 place-items-center rounded-md border border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 overflow-hidden flex-shrink-0">
                  {c.logoDataUrl ? (
                    <img src={c.logoDataUrl} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <Building2 className="h-5 w-5 text-slate-400 dark:text-slate-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.legalName}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {c.taxNumber} · {c.countryCode} · {c.defaultCurrency}
                  </div>
                </div>
              </div>
              {canManage && (
                <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
                  {hu.common.edit}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <UpsertCompanyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editTarget={editTarget}
      />

      {!canManage && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          A cégek kezeléséhez <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}
    </Card>
  )
}

function UpsertCompanyModal({
  open,
  onClose,
  editTarget,
}: {
  open: boolean
  onClose: () => void
  editTarget: Company | null
}) {
  const toast = useToast()
  const upsert = useUpsertCompany()
  const [legal, setLegal] = useState('')
  const [tax, setTax] = useState('')
  const [country, setCountry] = useState('HU')
  const [currency, setCurrency] = useState('HUF')
  /** undefined = nem változott; null = törölve; string = új data URL. */
  const [logoChange, setLogoChange] = useState<string | null | undefined>(undefined)
  const [logoError, setLogoError] = useState<string | null>(null)

  useEffect(() => {
    if (editTarget) {
      setLegal(editTarget.legalName)
      setTax(editTarget.taxNumber)
      setCountry(editTarget.countryCode)
      setCurrency(editTarget.defaultCurrency)
    } else {
      setLegal('')
      setTax('')
      setCountry('HU')
      setCurrency('HUF')
    }
    setLogoChange(undefined)
    setLogoError(null)
  }, [editTarget, open])

  async function handleLogoFile(file: File | null) {
    setLogoError(null)
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setLogoError('Csak képformátum (PNG/JPG/SVG/WebP) tölthető fel')
      return
    }
    if (file.size > 256 * 1024) {
      setLogoError('A logo max 256 KB lehet (kérjük, kicsinyítsd le)')
      return
    }
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(reader.error ?? new Error('FileReader hiba'))
        reader.readAsDataURL(file)
      })
      setLogoChange(dataUrl)
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Beolvasási hiba')
    }
  }

  const previewLogo =
    logoChange === undefined ? editTarget?.logoDataUrl ?? null : logoChange

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const saved = await upsert.mutateAsync({
        id: editTarget?.id,
        legalName: legal,
        taxNumber: tax,
        countryCode: country,
        defaultCurrency: currency,
        ...(logoChange !== undefined ? { logoDataUrl: logoChange } : {}),
      })
      toast.show({
        tone: 'success',
        title: editTarget ? 'Cég frissítve' : 'Új cég létrehozva',
        description: `${saved.legalName} · ${saved.taxNumber}`,
      })
      onClose()
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editTarget ? `${editTarget.legalName} szerkesztése` : 'Új cég'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Mégse
          </Button>
          <Button
            onClick={(e) => submit(e as unknown as React.FormEvent)}
            loading={upsert.isPending}
            disabled={!legal.trim() || !tax.trim()}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {editTarget ? hu.common.save : 'Létrehoz'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label htmlFor="co-name">Cég neve</Label>
          <Input
            id="co-name"
            value={legal}
            onChange={(e) => setLegal(e.target.value)}
            placeholder="pl. Adaptivo Kft."
            autoFocus
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="co-tax">Adószám</Label>
            <Input
              id="co-tax"
              value={tax}
              onChange={(e) => setTax(e.target.value)}
              placeholder="12345678-2-42"
            />
            <FieldHint>HU formátum: 8 számjegy + áfakód + megyekód</FieldHint>
          </div>
          <div>
            <Label htmlFor="co-country">Országkód</Label>
            <Input
              id="co-country"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              maxLength={2}
              placeholder="HU"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="co-currency">Alap deviza</Label>
          <Select
            id="co-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="HUF">HUF (Magyar forint)</option>
            <option value="EUR">EUR (Euró)</option>
            <option value="USD">USD (US dollár)</option>
          </Select>
        </div>

        <div>
          <Label htmlFor="co-logo">Cég logo</Label>
          <div className="flex items-start gap-3">
            <div className="grid h-16 w-16 place-items-center rounded-md border border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 overflow-hidden">
              {previewLogo ? (
                <img src={previewLogo} alt="Logo előnézet" className="h-full w-full object-contain" />
              ) : (
                <Building2 className="h-7 w-7 text-slate-400 dark:text-slate-500" />
              )}
            </div>
            <div className="flex-1 space-y-1">
              <input
                id="co-logo"
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => handleLogoFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-slate-700 dark:text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100 dark:file:bg-brand-900/30 dark:file:text-brand-200"
              />
              <FieldHint>PNG / JPG / SVG / WebP, max 256 KB</FieldHint>
              {previewLogo && (
                <button
                  type="button"
                  onClick={() => setLogoChange(null)}
                  className="text-xs text-status-error hover:underline"
                >
                  Logo eltávolítása
                </button>
              )}
              {logoError && <FieldError>{logoError}</FieldError>}
            </div>
          </div>
        </div>

        {upsert.isError && <FieldError>{(upsert.error as Error).message}</FieldError>}
      </form>
    </Modal>
  )
}

function NavSection() {
  const { activeRole, activeCompanyId, activeMembership } = useAuth()
  const canEdit = hasPermission(activeRole, 'settings.nav')
  const toast = useToast()

  const { data, isLoading } = useNavCredentials(activeCompanyId)
  const update = useUpdateNavCredentials()
  const testConn = useTestNavConnection()

  const [login, setLogin] = useState('')
  const [env, setEnv] = useState<'production' | 'test'>('production')
  const [password, setPassword] = useState('')
  const [signing, setSigning] = useState('')
  const [exch, setExch] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (data) {
      setLogin(data.navUserLogin)
      setEnv(data.navEnvironment)
      setPassword('')
      setSigning('')
      setExch('')
    }
  }, [data])

  if (!activeCompanyId) {
    return (
      <Card>
        <EmptyState title="Nincs aktív cég" description="Válassz egy céget a fejlécben." />
      </Card>
    )
  }
  if (isLoading || !data) return <CenterSpinner />

  async function save() {
    setError(null)
    try {
      await update.mutateAsync({
        companyId: activeCompanyId!,
        navUserLogin: login,
        navEnvironment: env,
        ...(password ? { password } : {}),
        ...(signing ? { signingKey: signing } : {}),
        ...(exch ? { exchangeKey: exch } : {}),
      })
      setPassword('')
      setSigning('')
      setExch('')
      toast.show({
        tone: 'success',
        title: 'NAV beállítás mentve',
        description: `${activeMembership?.legalName ?? 'cég'} — ${env === 'production' ? 'éles' : 'teszt'} környezet`,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ismeretlen hiba'
      setError(msg)
      toast.show({ tone: 'error', title: 'Mentés sikertelen', description: msg })
    }
  }

  async function ping() {
    try {
      const res = await testConn.mutateAsync(activeCompanyId!)
      toast.show({
        tone: 'success',
        title: 'NAV kapcsolat OK',
        description: `${activeMembership?.legalName ?? ''} · ${dateTime(res.lastPingAt!)}`,
      })
    } catch (err) {
      // A throw-olt hiba állapot már a `data.lastPingError`-be is bekerül (refetch után).
      toast.show({
        tone: 'error',
        title: 'NAV kapcsolat sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  // Állapotjelző badge
  const status = ((): { label: string; tone: 'ok' | 'warn' | 'error' | 'neutral'; Icon: typeof CheckCircle2 } => {
    if (!data.lastPingAt) return { label: 'Még nem tesztelt', tone: 'neutral', Icon: AlertTriangle }
    if (data.lastPingStatus === 'error') return { label: 'Hibás', tone: 'error', Icon: XCircle }
    if (data.lastPingStatus === 'ok') return { label: 'OK', tone: 'ok', Icon: CheckCircle2 }
    return { label: 'Ismeretlen', tone: 'neutral', Icon: AlertTriangle }
  })()

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>NAV technikai felhasználó</CardTitle>
          <CardSubtitle>
            Cég: <strong>{activeMembership?.legalName}</strong>. Az adatok envelope
            encryption-nel titkosítva tárolódnak — visszafejtve a UI-on soha nem jelennek meg.
          </CardSubtitle>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Badge tone={env === 'production' ? 'brand' : 'warn'}>
            <KeyRound className="h-3 w-3" /> {env === 'production' ? 'Production' : 'Teszt'} env
          </Badge>
          <Badge tone={status.tone}>
            <status.Icon className="h-3 w-3" /> Kapcsolat: {status.label}
          </Badge>
        </div>
      </CardHeader>

      {data.lastPingStatus === 'error' && data.lastPingError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:bg-red-950/30 dark:border-red-900/50">
          <div className="flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Utolsó teszt hibája:</div>
              <div className="text-xs mt-0.5">{data.lastPingError}</div>
              <div className="text-xs mt-0.5 opacity-75">
                Időpont: {data.lastPingAt ? dateTime(data.lastPingAt) : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="nav-login">Bejelentkezési név</Label>
          <Input
            id="nav-login"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            readOnly={!canEdit}
            placeholder="adaptivo_tech"
          />
          <FieldHint>NAV által kiadott technikai user login.</FieldHint>
        </div>
        <div>
          <Label htmlFor="nav-env">Környezet</Label>
          <Select
            id="nav-env"
            value={env}
            onChange={(e) => setEnv(e.target.value as 'production' | 'test')}
            disabled={!canEdit}
          >
            <option value="production">Éles (production)</option>
            <option value="test">Teszt</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="nav-pass">Jelszó {data.hasPassword && <span className="text-xs text-emerald-700 ml-1">· beállítva</span>}</Label>
          <Input
            id="nav-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            readOnly={!canEdit}
            placeholder={data.hasPassword ? 'Új jelszó beírásával felülírod' : 'Add meg a jelszót'}
          />
        </div>
        <div>
          <Label htmlFor="nav-sign">
            Aláíró kulcs {data.hasSigningKey && <span className="text-xs text-emerald-700 ml-1">· beállítva</span>}
          </Label>
          <Input
            id="nav-sign"
            type="password"
            value={signing}
            onChange={(e) => setSigning(e.target.value)}
            readOnly={!canEdit}
            placeholder={data.hasSigningKey ? 'Csak ha rotálod' : 'Add meg az aláíró kulcsot'}
          />
        </div>
        <div>
          <Label htmlFor="nav-ex">
            Csere kulcs {data.hasExchangeKey && <span className="text-xs text-emerald-700 ml-1">· beállítva</span>}
          </Label>
          <Input
            id="nav-ex"
            type="password"
            value={exch}
            onChange={(e) => setExch(e.target.value)}
            readOnly={!canEdit}
            placeholder={data.hasExchangeKey ? 'Csak ha rotálod' : 'Add meg a csere kulcsot'}
          />
        </div>
      </div>

      {error && <div className="mt-3"><FieldError>{error}</FieldError></div>}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
        <p className="text-xs text-slate-500">
          Utolsó sikeres ping: {data.lastPingAt ? dateTime(data.lastPingAt) : '—'} · Utolsó rotáció:{' '}
          {data.lastRotatedAt ? dateTime(data.lastRotatedAt) : '—'}
        </p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={ping}
            loading={testConn.isPending}
            disabled={!canEdit}
          >
            Kapcsolat teszt
          </Button>
          <Button
            size="sm"
            onClick={save}
            loading={update.isPending}
            disabled={!canEdit}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {hu.common.save}
          </Button>
        </div>
      </div>

      {!canEdit && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          A NAV kredenciálok szerkesztéséhez <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}

      <NavAuditMiniPanel companyId={activeCompanyId} />
    </Card>
  )
}

function NavAuditMiniPanel({ companyId }: { companyId: string }) {
  const { data } = useAuditLogs('nav_credentials')
  const events = (data ?? []).filter((e) => e.subjectId === companyId).slice(0, 20)
  const [open, setOpen] = useState(false)

  if (events.length === 0) return null

  const failureCount = events.filter((e) => e.outcome === 'failure').length

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="mt-4 rounded-md border border-slate-200 dark:border-slate-800"
    >
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded-md">
        <span className="inline-flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          NAV művelet napló
          {failureCount > 0 && (
            <Badge tone="error">
              <AlertTriangle className="h-3 w-3" /> {failureCount} hiba
            </Badge>
          )}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">{events.length} bejegyzés</span>
      </summary>
      <ul className="max-h-72 overflow-y-auto scrollbar-thin border-t border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
        {events.map((e) => (
          <li key={e.id} className="px-3 py-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <Badge tone={e.outcome === 'failure' ? 'error' : e.outcome === 'success' ? 'ok' : 'info'}>
                {e.action}
              </Badge>
              <span className="text-slate-500 dark:text-slate-400 tabular-nums">{dateTime(e.createdAt)}</span>
            </div>
            {e.metadata && Object.keys(e.metadata).length > 0 && (
              <div className="mt-1 font-mono text-slate-600 dark:text-slate-400 break-all">
                {Object.entries(e.metadata)
                  .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                  .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                  .join(' · ')}
              </div>
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}

function EmailSection() {
  const { activeRole } = useAuth()
  const canEdit = hasPermission(activeRole, 'settings.email')
  const toast = useToast()

  const { data, isLoading } = useEmailAccount()
  const update = useUpdateEmailAccount()
  const testConn = useTestEmailConnection()
  const listFolders = useListImapFolders()

  const [address, setAddress] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState<number>(993)
  const [username, setUsername] = useState('')
  const [folder, setFolder] = useState('INBOX')
  const [folderOptions, setFolderOptions] = useState<string[]>([])
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (data) {
      setAddress(data.address)
      setHost(data.imapHost ?? '')
      setPort(data.imapPort ?? 993)
      setUsername(data.imapUsername ?? '')
      setFolder(data.imapFolder || 'INBOX')
      setPassword('')
    }
  }, [data])

  if (isLoading || !data) return <CenterSpinner />

  async function save() {
    setError(null)
    try {
      await update.mutateAsync({
        address,
        imapHost: host,
        imapPort: Number(port),
        imapUsername: username,
        imapFolder: folder.trim() || 'INBOX',
        ...(password ? { password } : {}),
      })
      setPassword('')
      toast.show({ tone: 'success', title: 'Email fiók mentve', description: `${address} · ${folder.trim() || 'INBOX'}` })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ismeretlen hiba'
      setError(msg)
      toast.show({ tone: 'error', title: 'Mentés sikertelen', description: msg })
    }
  }

  async function ping() {
    try {
      const res = await testConn.mutateAsync()
      toast.show({
        tone: res.status === 'active' ? 'success' : 'error',
        title: res.status === 'active' ? 'IMAP kapcsolat OK' : 'IMAP kapcsolat hibás',
        description: res.lastSyncAt ? dateTime(res.lastSyncAt) : '',
      })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Teszt sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  async function browseFolders() {
    setError(null)
    try {
      const res = await listFolders.mutateAsync()
      setFolderOptions(res.folders)
      toast.show({
        tone: 'success',
        title: 'Mappák betöltve',
        description: `${res.folders.length} mappa — válassz a listából vagy írd be kézzel.`,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ismeretlen hiba'
      setError(msg)
      toast.show({
        tone: 'error',
        title: 'Mappák betöltése sikertelen',
        description: msg,
      })
    }
  }

  const statusTone = data.status === 'active' ? 'ok' : data.status === 'error' ? 'error' : 'warn'
  const statusLabel = data.status === 'active' ? 'Aktív' : data.status === 'error' ? 'Hiba' : 'Szüneteltetve'

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Email fiók — {data.address}</CardTitle>
          <CardSubtitle>
            IMAP IDLE poll, az eredeti emaileket és mellékleteket az object store-ba archiváljuk.
            Utolsó sync: {data.lastSyncAt ? dateTime(data.lastSyncAt) : '—'}
          </CardSubtitle>
        </div>
        <Badge tone={statusTone}>{statusLabel}</Badge>
      </CardHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label htmlFor="imap-address">Email cím</Label>
          <Input
            id="imap-address"
            type="email"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            readOnly={!canEdit}
            placeholder="szamla@adaptivo.hu"
          />
        </div>
        <div>
          <Label htmlFor="imap-host">IMAP host</Label>
          <Input
            id="imap-host"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            readOnly={!canEdit}
            placeholder="imap.adaptivo.hu"
          />
        </div>
        <div>
          <Label htmlFor="imap-port">Port</Label>
          <Input
            id="imap-port"
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => setPort(Number(e.target.value))}
            readOnly={!canEdit}
          />
        </div>
        <div>
          <Label htmlFor="imap-user">IMAP felhasználónév</Label>
          <Input
            id="imap-user"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            readOnly={!canEdit}
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="imap-folder">Szinkronizálandó mappa</Label>
          <div className="flex gap-2">
            <Input
              id="imap-folder"
              list="imap-folder-options"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              readOnly={!canEdit}
              placeholder="INBOX"
              className="flex-1"
            />
            <datalist id="imap-folder-options">
              {folderOptions.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={browseFolders}
              loading={listFolders.isPending}
              disabled={!canEdit}
            >
              Mappák betöltése
            </Button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Tallózáshoz kattints a <strong>Mappák betöltése</strong> gombra (valódi IMAP bejelentkezés), vagy
            írd be kézzel a mappa nevét. Átirányított / aliasos számláknál gyakran nem az <code>INBOX</code>,
            hanem egy külön mappa kell. Alapértelmezett: <code>INBOX</code>.
          </p>
        </div>
        <div>
          <Label htmlFor="imap-pass">
            Jelszó {data.hasPassword && <span className="text-xs text-emerald-700 ml-1">· beállítva</span>}
          </Label>
          <Input
            id="imap-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            readOnly={!canEdit}
            placeholder={data.hasPassword ? 'Új jelszó beírásával felülírod' : 'Add meg a jelszót'}
          />
        </div>
      </div>

      {error && <div className="mt-3"><FieldError>{error}</FieldError></div>}

      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={ping}
          loading={testConn.isPending}
          disabled={!canEdit}
        >
          Kapcsolat teszt
        </Button>
        <Button
          size="sm"
          onClick={save}
          loading={update.isPending}
          disabled={!canEdit}
          leftIcon={<Save className="h-4 w-4" />}
        >
          {hu.common.save}
        </Button>
      </div>

      {!canEdit && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          Az email fiók szerkesztéséhez <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}
    </Card>
  )
}

function OutgoingEmailSection() {
  const { activeRole } = useAuth()
  const canEdit = hasPermission(activeRole, 'settings.email')
  const toast = useToast()

  const { data, isLoading } = useOutgoingEmail()
  const update = useUpdateOutgoingEmail()
  const testConn = useTestOutgoingEmail()

  const [provider, setProvider] = useState<OutgoingEmailSettings['provider']>('smtp')
  const [fromAddress, setFromAddress] = useState('')
  const [fromName, setFromName] = useState('')
  const [replyTo, setReplyTo] = useState('')
  const [host, setHost] = useState('')
  const [port, setPort] = useState(587)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [encryption, setEncryption] = useState<OutgoingEmailSettings['encryption']>('starttls')
  const [enabled, setEnabled] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (data) {
      setProvider(data.provider)
      setFromAddress(data.fromAddress)
      setFromName(data.fromName ?? '')
      setReplyTo(data.replyToAddress ?? '')
      setHost(data.smtpHost ?? '')
      setPort(data.smtpPort ?? 587)
      setUsername(data.smtpUsername ?? '')
      setPassword('')
      setEncryption(data.encryption)
      setEnabled(data.enabled)
    }
  }, [data])

  if (isLoading || !data) return <CenterSpinner />

  async function save() {
    setError(null)
    try {
      await update.mutateAsync({
        provider,
        fromAddress,
        fromName: fromName,
        replyToAddress: replyTo || null,
        smtpHost: host,
        smtpPort: Number(port),
        smtpUsername: username,
        ...(password ? { password } : {}),
        encryption,
        enabled,
      })
      setPassword('')
      toast.show({ tone: 'success', title: 'Kimenő email mentve', description: fromAddress })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ismeretlen hiba'
      setError(msg)
      toast.show({ tone: 'error', title: 'Mentés sikertelen', description: msg })
    }
  }

  async function ping() {
    try {
      const res = await testConn.mutateAsync()
      toast.show({
        tone: 'success',
        title: 'SMTP kapcsolat OK',
        description: `${res.fromAddress} · ${res.provider}`,
      })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'SMTP kapcsolat sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  const status = ((): { label: string; tone: 'ok' | 'warn' | 'error' | 'neutral'; Icon: typeof CheckCircle2 } => {
    if (!data.enabled) return { label: 'Kikapcsolva', tone: 'neutral', Icon: AlertTriangle }
    if (!data.lastTestAt) return { label: 'Még nem tesztelt', tone: 'warn', Icon: AlertTriangle }
    if (data.lastTestStatus === 'error') return { label: 'Hibás', tone: 'error', Icon: XCircle }
    if (data.lastTestStatus === 'ok') return { label: 'OK', tone: 'ok', Icon: CheckCircle2 }
    return { label: 'Ismeretlen', tone: 'neutral', Icon: AlertTriangle }
  })()

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Kimenő email (SMTP / relay)</CardTitle>
          <CardSubtitle>
            Innen mennek a meghívók, jóváhagyási értesítések és a könyvelőnek küldött csomagok.
            A bejövő (számlafogadó) email <strong>külön van</strong> a "Bejövő email" menüben.
          </CardSubtitle>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Badge tone={status.tone}>
            <status.Icon className="h-3 w-3" /> {status.label}
          </Badge>
          {data.lastTestAt && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              Teszt: {dateTime(data.lastTestAt)}
            </span>
          )}
        </div>
      </CardHeader>

      {data.lastTestStatus === 'error' && data.lastTestError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:bg-red-950/30 dark:border-red-900/50">
          <div className="flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Utolsó teszt hibája:</div>
              <div className="text-xs mt-0.5">{data.lastTestError}</div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="oe-provider">Provider</Label>
            <Select
              id="oe-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value as OutgoingEmailSettings['provider'])}
              disabled={!canEdit}
            >
              <option value="smtp">SMTP (saját szerver)</option>
              <option value="ses">Amazon SES</option>
              <option value="sendgrid">SendGrid</option>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={!canEdit}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-slate-700 dark:text-slate-300">Kimenő email bekapcsolva</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="oe-from">Feladó cím (From)</Label>
            <Input
              id="oe-from"
              type="email"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              placeholder="noreply@adaptivo.hu"
              readOnly={!canEdit}
            />
            <FieldHint>Innen megy minden kimenő üzenet.</FieldHint>
          </div>
          <div>
            <Label htmlFor="oe-fromname">Feladó megjelenítő név</Label>
            <Input
              id="oe-fromname"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Adaptivo Számlakezelő"
              readOnly={!canEdit}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="oe-replyto">Reply-To cím (opcionális)</Label>
            <Input
              id="oe-replyto"
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="penzugy@adaptivo.hu"
              readOnly={!canEdit}
            />
            <FieldHint>Ha a címzettek nem a noreply címre, hanem ide válaszoljanak.</FieldHint>
          </div>
        </div>

        {provider === 'smtp' && (
          <div className="rounded-md border border-slate-200 dark:border-slate-800 p-3 space-y-3">
            <h4 className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">
              SMTP szerver
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="oe-host">SMTP host</Label>
                <Input
                  id="oe-host"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="smtp.gmail.com"
                  readOnly={!canEdit}
                />
              </div>
              <div>
                <Label htmlFor="oe-port">Port</Label>
                <Input
                  id="oe-port"
                  type="number"
                  min={1}
                  max={65535}
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                  readOnly={!canEdit}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="oe-user">Felhasználónév</Label>
                <Input
                  id="oe-user"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  readOnly={!canEdit}
                />
              </div>
              <div>
                <Label htmlFor="oe-pass">
                  Jelszó {data.hasPassword && <span className="text-xs text-emerald-700 ml-1">· beállítva</span>}
                </Label>
                <Input
                  id="oe-pass"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={data.hasPassword ? 'Új jelszó beírásával felülírod' : 'SMTP jelszó'}
                  readOnly={!canEdit}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="oe-enc">Titkosítás</Label>
              <Select
                id="oe-enc"
                value={encryption}
                onChange={(e) => setEncryption(e.target.value as OutgoingEmailSettings['encryption'])}
                disabled={!canEdit}
              >
                <option value="starttls">STARTTLS (port 587)</option>
                <option value="tls">TLS (port 465)</option>
                <option value="none">Titkosítás nélkül (NEM ajánlott)</option>
              </Select>
            </div>
          </div>
        )}

        {provider !== 'smtp' && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200">
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
            A {provider === 'ses' ? 'SES' : 'SendGrid'} provider később lesz implementálva — most válaszd
            az <strong>SMTP</strong> opciót (saját szerver, Gmail, Mailgun-SMTP, stb.) és add meg a host /
            felhasználónév / jelszó adatokat.
          </div>
        )}
      </div>

      {error && <div className="mt-3"><FieldError>{error}</FieldError></div>}

      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={ping}
          loading={testConn.isPending}
          disabled={!canEdit}
        >
          Kapcsolat teszt
        </Button>
        <Button
          size="sm"
          onClick={save}
          loading={update.isPending}
          disabled={!canEdit}
          leftIcon={<Save className="h-4 w-4" />}
        >
          {hu.common.save}
        </Button>
      </div>

      {!canEdit && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          A kimenő email beállításához <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}
    </Card>
  )
}

function SzamlazzSection() {
  const { activeCompanyId, activeMembership, activeRole } = useAuth()
  const canEdit = hasPermission(activeRole, 'settings.nav')
  const toast = useToast()
  const { data, isLoading } = useSzamlazz(activeCompanyId) as {
    data: { companyId: string | null; hasAgentKey: boolean; enabled: boolean; lastUsedAt: string | null; lastTestAt: string | null; lastTestStatus: 'ok' | 'error' | null; lastTestError: string | null } | undefined
    isLoading: boolean
  }
  const update = useUpdateSzamlazz()
  const testKey = useTestSzamlazz()
  const sync = useSyncSzamlazz()

  const [agentKey, setAgentKey] = useState('')
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled)
      setAgentKey('')
    }
  }, [data])

  if (!activeCompanyId) {
    return (
      <Card>
        <EmptyState title="Nincs aktív cég" description="Válassz egy céget a fejlécben." />
      </Card>
    )
  }
  if (isLoading || !data) return <CenterSpinner />

  async function save() {
    try {
      await update.mutateAsync({
        companyId: activeCompanyId!,
        payload: {
          ...(agentKey ? { agentKey } : {}),
          enabled,
        },
      })
      setAgentKey('')
      toast.show({
        tone: 'success',
        title: 'Számlázz.hu beállítás mentve',
        description: `${activeMembership?.legalName} · ${enabled ? 'engedélyezve' : 'kikapcsolva'}`,
      })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Mentés sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  async function ping() {
    try {
      const res = await testKey.mutateAsync(activeCompanyId!)
      toast.show({
        tone: res.ok ? 'success' : 'error',
        title: res.ok ? 'Számlázz.hu kulcs OK' : 'Kulcs hibás',
        description: res.detail,
      })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Teszt sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  async function runSync() {
    try {
      const res = await sync.mutateAsync(activeCompanyId!)
      toast.show({
        tone: res.status === 'ok' ? 'success' : 'info',
        title: res.status === 'ok' ? 'Számlázz.hu sync OK' : 'Számlázz.hu sync',
        description: res.detail,
      })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Sync sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  const status = ((): { label: string; tone: 'ok' | 'warn' | 'error' | 'neutral'; Icon: typeof CheckCircle2 } => {
    if (!data.enabled) return { label: 'Kikapcsolva', tone: 'neutral', Icon: AlertTriangle }
    if (!data.lastTestAt) return { label: 'Még nem tesztelt', tone: 'warn', Icon: AlertTriangle }
    if (data.lastTestStatus === 'error') return { label: 'Hibás', tone: 'error', Icon: XCircle }
    if (data.lastTestStatus === 'ok') return { label: 'OK', tone: 'ok', Icon: CheckCircle2 }
    return { label: 'Ismeretlen', tone: 'neutral', Icon: AlertTriangle }
  })()

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Számlázz.hu — saját kimenő számlák</CardTitle>
          <CardSubtitle>
            Cég: <strong>{activeMembership?.legalName}</strong>. Az Agent kulcs envelope encryption-nel
            titkosítva tárolódik. A sync az utolsó 30 napos kimenő számláidat letölti és bekerülnek
            "Kimenő" jellegű invoice-okká.
          </CardSubtitle>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <Badge tone={status.tone}>
            <status.Icon className="h-3 w-3" /> {status.label}
          </Badge>
          {data.lastUsedAt && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              Utolsó sync: {dateTime(data.lastUsedAt)}
            </span>
          )}
        </div>
      </CardHeader>

      {data.lastTestStatus === 'error' && data.lastTestError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:bg-red-950/30 dark:border-red-900/50">
          <div className="flex items-start gap-2 text-sm text-red-800 dark:text-red-200">
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Utolsó teszt hibája:</div>
              <div className="text-xs mt-0.5">{data.lastTestError}</div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <Label htmlFor="szl-key">
            Agent kulcs {data.hasAgentKey && <span className="text-xs text-emerald-700 ml-1">· beállítva</span>}
          </Label>
          <Input
            id="szl-key"
            type="password"
            value={agentKey}
            onChange={(e) => setAgentKey(e.target.value)}
            placeholder={data.hasAgentKey ? 'Új kulcs beírásával felülírod' : 'Add meg a Számla Agent kulcsot'}
            readOnly={!canEdit}
          />
          <FieldHint>
            A kulcsot a Számlázz.hu fiókod alatt: Beállítások → Számla Agent → Új kulcs.
          </FieldHint>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!canEdit}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-slate-700 dark:text-slate-300">Számlázz.hu integráció bekapcsolva</span>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={ping}
          loading={testKey.isPending}
          disabled={!canEdit || !data.hasAgentKey}
        >
          Kulcs teszt
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={runSync}
          loading={sync.isPending}
          disabled={!canEdit || !data.hasAgentKey || !data.enabled}
        >
          Sync most
        </Button>
        <Button
          size="sm"
          onClick={save}
          loading={update.isPending}
          disabled={!canEdit}
          leftIcon={<Save className="h-4 w-4" />}
        >
          {hu.common.save}
        </Button>
      </div>

      {!canEdit && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          A Számlázz.hu beállításához <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}
    </Card>
  )
}

function AccountantSection() {
  const { activeCompanyId, activeMembership, activeRole } = useAuth()
  const canEdit = hasPermission(activeRole, 'settings.approvals')
  const toast = useToast()

  const { data, isLoading } = useCompanySettings(activeCompanyId)
  const update = useUpdateCompanySettings()

  const [accName, setAccName] = useState('')
  const [accEmail, setAccEmail] = useState('')
  const [format, setFormat] = useState<CompanySettings['exportFormat']>('excel')
  const [period, setPeriod] = useState<CompanySettings['vatPeriod']>('monthly')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (data) {
      setAccName(data.accountantName ?? '')
      setAccEmail(data.accountantEmail ?? '')
      setFormat(data.exportFormat)
      setPeriod(data.vatPeriod)
    }
  }, [data])

  if (!activeCompanyId) {
    return (
      <Card>
        <EmptyState title="Nincs aktív cég" description="Válassz egy céget a fejlécben." />
      </Card>
    )
  }
  if (isLoading || !data) return <CenterSpinner />

  async function save() {
    setError(null)
    try {
      await update.mutateAsync({
        companyId: activeCompanyId!,
        payload: {
          accountantName: accName,
          accountantEmail: accEmail,
          exportFormat: format,
          vatPeriod: period,
        },
      })
      toast.show({
        tone: 'success',
        title: 'Könyvelő beállítások mentve',
        description: `${activeMembership?.legalName} — ${accEmail}`,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ismeretlen hiba'
      setError(msg)
      toast.show({ tone: 'error', title: 'Mentés sikertelen', description: msg })
    }
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Könyvelő</CardTitle>
          <CardSubtitle>
            Cég: <strong>{activeMembership?.legalName}</strong>. Havi átadócsomag címzettje és
            formátuma. A küldéskor (Havi zárás → Küldés a könyvelőnek) ezt használjuk default-ként.
          </CardSubtitle>
        </div>
      </CardHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="acc-name">Könyvelő neve</Label>
          <Input
            id="acc-name"
            value={accName}
            onChange={(e) => setAccName(e.target.value)}
            readOnly={!canEdit}
            placeholder="pl. Szabó Gábor (KKV-Könyv Kft.)"
          />
        </div>
        <div>
          <Label htmlFor="acc-email">Email cím</Label>
          <Input
            id="acc-email"
            type="email"
            value={accEmail}
            onChange={(e) => setAccEmail(e.target.value)}
            readOnly={!canEdit}
            placeholder="konyvelo@example.hu"
          />
        </div>
        <div>
          <Label htmlFor="acc-format">Export formátum</Label>
          <Select
            id="acc-format"
            value={format}
            onChange={(e) => setFormat(e.target.value as CompanySettings['exportFormat'])}
            disabled={!canEdit}
          >
            <option value="excel">Excel (.xlsx)</option>
            <option value="csv">CSV</option>
            <option value="both">Mindkettő</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="acc-period">ÁFA periódus</Label>
          <Select
            id="acc-period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as CompanySettings['vatPeriod'])}
            disabled={!canEdit}
          >
            <option value="monthly">Havi</option>
            <option value="quarterly">Negyedéves</option>
            <option value="yearly">Éves</option>
          </Select>
        </div>
      </div>

      {error && <div className="mt-3"><FieldError>{error}</FieldError></div>}

      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button
          size="sm"
          onClick={save}
          loading={update.isPending}
          disabled={!canEdit}
          leftIcon={<Save className="h-4 w-4" />}
        >
          {hu.common.save}
        </Button>
      </div>

      {!canEdit && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          A könyvelői beállításhoz <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}
    </Card>
  )
}

function UsersSection() {
  const { activeRole } = useAuth()
  const canManage = hasPermission(activeRole, 'settings.users')
  const { data, isLoading } = useUsers()
  const [open, setOpen] = useState(false)

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Felhasználók</CardTitle>
          <CardSubtitle>
            Tenant alatti userek és szerepeik a cégeknél. Önregisztráció nincs — admin / cégvezető hozza létre.
          </CardSubtitle>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setOpen(true)} leftIcon={<UserPlus className="h-4 w-4" />}>
            Új user
          </Button>
        )}
      </CardHeader>

      {isLoading ? (
        <CenterSpinner />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Nincs felhasználó" description="Még senki sincs a tenanten." />
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {data.map((u) => (
            <li key={u.id} className="flex items-start justify-between py-3 gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  {u.fullName}
                  {u.mfaEnabled && (
                    <Badge tone="ok">
                      <ShieldCheck className="h-3 w-3" /> MFA
                    </Badge>
                  )}
                  {u.status !== 'active' && <Badge tone="neutral">{u.status}</Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {u.memberships.map((m) => (
                    <Badge key={m.companyId} tone="neutral">
                      {m.legalName} · {hu.roles[m.role]}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 text-xs text-slate-500 flex-shrink-0">
                {u.lastLoginAt ? `Utolsó: ${dateTime(u.lastLoginAt)}` : 'Még nem lépett be'}
                <Button variant="ghost" size="sm" disabled>
                  {hu.common.edit}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <CreateUserModal open={open} onClose={() => setOpen(false)} />

      {!canManage && (
        <p className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <ShieldCheck className="inline h-3 w-3 mr-1" />
          A felhasználók szerkesztéséhez <strong>{hu.roles.admin}</strong> vagy{' '}
          <strong>{hu.roles.company_owner}</strong> szerep szükséges.
        </p>
      )}
    </Card>
  )
}

const ROLE_OPTIONS: Role[] = [
  'company_owner',
  'finance_manager',
  'employee_uploader',
  'accountant',
  'readonly_auditor',
]

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const companies = useCompanies()
  const create = useCreateUser()
  const outgoing = useOutgoingEmail()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [companyId, setCompanyId] = useState<UUID>('')
  const [role, setRole] = useState<Role>('finance_manager')
  const [sendInvite, setSendInvite] = useState(false)

  const outgoingReady =
    !!outgoing.data?.enabled &&
    !!outgoing.data.fromAddress &&
    (outgoing.data.provider !== 'smtp' ||
      (!!outgoing.data.smtpHost && !!outgoing.data.smtpUsername && outgoing.data.hasPassword))

  useEffect(() => {
    // Ha nincs SMTP, ne legyen bepipálva alapból
    if (!outgoingReady) setSendInvite(false)
  }, [outgoingReady])

  function reset() {
    setFullName('')
    setEmail('')
    setCompanyId(companies.data?.[0]?.id ?? '')
    setRole('finance_manager')
    setSendInvite(true)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const effectiveCompany = companyId || companies.data?.[0]?.id
      if (!effectiveCompany) throw new Error('Nincs választható cég')
      const created = await create.mutateAsync({
        fullName,
        email,
        companyId: effectiveCompany,
        role,
        sendInvite,
      })
      toast.show({
        tone: 'success',
        title: 'Felhasználó létrehozva',
        description: `${created.fullName} · ${created.email}`,
      })
      reset()
      onClose()
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'Hiba',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Új felhasználó"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Mégse
          </Button>
          <Button
            onClick={(e) => submit(e as unknown as React.FormEvent)}
            loading={create.isPending}
            disabled={!fullName.trim() || !email.trim() || !role}
            leftIcon={<UserPlus className="h-4 w-4" />}
          >
            Létrehoz
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="u-name">Teljes név</Label>
            <Input
              id="u-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Pl. Kovács Anna"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="u-email">Email cím</Label>
            <Input
              id="u-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="anna@adaptivo.hu"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="u-company">Cég</Label>
            <Select
              id="u-company"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              disabled={companies.isLoading}
            >
              <option value="">Válassz céget…</option>
              {companies.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.legalName}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="u-role">Szerep</Label>
            <Select id="u-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {hu.roles[r]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <label
          className={cn(
            'flex items-start gap-2 rounded border px-3 py-2 text-sm',
            outgoingReady
              ? 'border-slate-200 bg-slate-50/60 dark:bg-slate-800/40 dark:border-slate-700'
              : 'border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900/50',
          )}
        >
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
            disabled={!outgoingReady}
          />
          <span>
            <span className="font-medium">Meghívó email küldése</span>
            {outgoingReady ? (
              <span className="block text-xs text-slate-600 dark:text-slate-300">
                A user egy aktiváló linket kap a megadott emailre — feladó:{' '}
                <code className="text-[11px]">{outgoing.data?.fromAddress}</code>. Production-ben az
                első belépéskor jelszót állít.
              </span>
            ) : (
              <span className="block text-xs text-amber-800 dark:text-amber-200">
                <AlertTriangle className="inline h-3 w-3 mr-1" />
                Nincs kimenő email beállítva — meghívó nem küldhető. Konfiguráld a{' '}
                <strong>Beállítások → Kimenő email (SMTP)</strong> menüpontban.
              </span>
            )}
          </span>
        </label>

        <FieldHint>
          Production-ben SSO + MFA jön (lásd <code>docs/07-security.md</code> 7.4). Önregisztráció a
          designnál tiltott — minden új usert admin / cégvezető hoz létre, és minden művelet az audit logba
          kerül.
        </FieldHint>

        {create.isError && <FieldError>{(create.error as Error).message}</FieldError>}
      </form>
    </Modal>
  )
}

function AuditSection() {
  const [filter, setFilter] = useState<string>('')
  const { data, isLoading } = useAuditLogs(filter || undefined)

  const items = data ?? []
  const distinctSubjects = Array.from(new Set(items.map((e) => e.subjectType))).sort()

  const toneFor = (outcome: 'success' | 'failure' | 'info') =>
    outcome === 'success' ? 'ok' : outcome === 'failure' ? 'error' : 'info'

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Audit log</CardTitle>
          <CardSubtitle>
            Append-only — minden mutáció itt landol. Production-ben WORM bucketbe is tükrözve.
            {' '}Auto-refresh: 5 mp.
          </CardSubtitle>
        </div>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-9 w-44 text-sm"
        >
          <option value="">Minden subject</option>
          {distinctSubjects.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      </CardHeader>

      {isLoading ? (
        <CenterSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          title="Még nincs audit bejegyzés"
          description="Hajts végre néhány műveletet (mentés, jóváhagyás, user létrehozás), és itt fog megjelenni."
        />
      ) : (
        <ul className="space-y-1.5 text-sm max-h-[60vh] overflow-y-auto pr-1 scrollbar-thin">
          {items.map((e) => (
            <li key={e.id} className="rounded border border-slate-200 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2">
                  <Badge tone={toneFor(e.outcome)}>{e.action}</Badge>
                  <span className="text-xs text-slate-500">{e.subjectType}</span>
                </span>
                <span className="text-xs text-slate-500 tabular-nums">{dateTime(e.createdAt)}</span>
              </div>
              <div className="mt-1 text-xs text-slate-600">
                Actor: {e.actorName ?? '(rendszer)'}
                {e.actorRole && <span className="ml-1 text-slate-400">· {hu.roles[e.actorRole]}</span>}
                {e.subjectId && (
                  <span className="ml-1 text-slate-400 font-mono">· {e.subjectId.slice(0, 12)}</span>
                )}
              </div>
              {e.metadata && Object.keys(e.metadata).length > 0 && (
                <>
                  {/* note külön mezőként, olvashatóan — pl. NAV V1.2 figyelmeztetés */}
                  {typeof e.metadata.note === 'string' && (
                    <div className="mt-1 text-xs text-slate-700 dark:text-slate-300 italic">
                      → {e.metadata.note}
                    </div>
                  )}
                  {/* perCompany breakdown — strukturált megjelenítés array esetén */}
                  {Array.isArray((e.metadata as Record<string, unknown>).perCompany) && (
                    <ul className="mt-1 text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
                      {((e.metadata as { perCompany: Array<{ company: string; ok: boolean; detail: string }> }).perCompany).map((c, i) => (
                        <li key={i} className="font-mono">
                          {c.ok ? '✓' : '✗'} <strong>{c.company}</strong> · {c.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* többi mező egysoros formában */}
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
                    {Object.entries(e.metadata)
                      .filter(([k, v]) => v !== null && v !== undefined && v !== '' && k !== 'note' && k !== 'perCompany')
                      .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v).slice(0, 60)}`)
                      .join(' · ')}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// AutomationSection — cron jobok ki/be kapcsolása + utolsó futás + "Most futtass"
// ─────────────────────────────────────────────────────────────────────────────

const JOB_INFO: Record<
  string,
  { label: string; description: string; settingKey: keyof CompanySettings; intervalLabel: string }
> = {
  'cron.nav-sync': {
    label: 'NAV szinkron',
    description: 'NAV Online Számláról minden 6 órában letölti a bejövő számlákat (utolsó 35 nap).',
    settingKey: 'automationNavSync',
    intervalLabel: '6 óránként',
  },
  'cron.email-sync': {
    label: 'IMAP email letöltés',
    description: 'Az IMAP fiókról óránként letölti az új mellékleteket (PDF / XML / kép).',
    settingKey: 'automationEmailSync',
    intervalLabel: 'óránként',
  },
  'cron.szamlazz-sync': {
    label: 'Számlázz.hu kimenő',
    description: 'A Számlázz.hu API-ról 6 óránként letölti a saját kimenő számlákat (utolsó 30 nap).',
    settingKey: 'automationSzamlazzSync',
    intervalLabel: '6 óránként',
  },
  'cron.overdue-check': {
    label: 'Lejárt számla figyelmeztetés',
    description: 'Napi egy körben átnézi a lejárt esedékességű fizetetlen számlákat, és értesítést küld a felelősnek.',
    settingKey: 'automationOverdueCheck',
    intervalLabel: 'naponta',
  },
}

function AutomationSection() {
  const { activeCompanyId, activeRole } = useAuth()
  const canEdit = activeRole === 'admin' || activeRole === 'company_owner'
  const toast = useToast()

  const settingsQ = useCompanySettings(activeCompanyId)
  const statusQ = useCronStatus()
  const runsQ = useCronRuns(30)
  const update = useUpdateCompanySettings()
  const runJob = useRunCronJob()

  if (!activeCompanyId) {
    return (
      <Card>
        <EmptyState title="Nincs aktív cég" description="Válassz egy céget a fejlécben." />
      </Card>
    )
  }
  if (settingsQ.isLoading || !settingsQ.data) return <CenterSpinner />

  const settings = settingsQ.data
  const jobs = statusQ.data?.jobs ?? []

  async function toggleFlag(key: keyof CompanySettings, next: boolean) {
    await update.mutateAsync({
      companyId: activeCompanyId!,
      payload: { [key]: next } as Record<string, unknown>,
    })
    toast.show({ tone: 'success', title: next ? 'Bekapcsolva' : 'Kikapcsolva' })
  }

  async function runNow(type: string) {
    try {
      const r = await runJob.mutateAsync(type)
      toast.show({ tone: 'success', title: 'Futás kész', description: r.summary || '—' })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : String(err) })
    }
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Automatizálás</CardTitle>
          <CardSubtitle>
            A háttérben futó szinkronok és figyelők. Ki/bekapcsolhatóak, és kézzel is futtathatóak.
          </CardSubtitle>
        </div>
      </CardHeader>
      <div className="divide-y divide-slate-200 dark:divide-slate-700">
        {Object.entries(JOB_INFO).map(([type, info]) => {
          const status = jobs.find((j) => j.type === type)
          const enabled = !!(settings[info.settingKey] ?? true)
          return (
            <div key={type} className="p-4 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{info.label}</span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {info.intervalLabel}
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{info.description}</p>
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Utolsó:{' '}
                    {status?.lastRunAt ? (
                      <>
                        {dateTime(status.lastRunAt)}{' '}
                        {status.lastStatus === 'success' ? (
                          <CheckCircle2 className="h-3 w-3 inline text-emerald-600" />
                        ) : status.lastStatus === 'failed' ? (
                          <XCircle className="h-3 w-3 inline text-red-600" />
                        ) : null}
                      </>
                    ) : (
                      <span className="italic">még nem futott</span>
                    )}
                  </span>
                  {status?.lastSummary && (
                    <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-md">
                      {status.lastSummary}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => runNow(type)}
                  loading={runJob.isPending && runJob.variables === type}
                  disabled={!canEdit}
                  leftIcon={<Play className="h-3 w-3" />}
                >
                  Most futtass
                </Button>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={enabled}
                    disabled={!canEdit}
                    onChange={(e) => toggleFlag(info.settingKey, e.target.checked)}
                  />
                  <span className="text-xs">{enabled ? 'be' : 'ki'}</span>
                </label>
              </div>
            </div>
          )
        })}
      </div>

      {/* Futás-napló — az utolsó 30 cron + manuális futás */}
      <div className="border-t border-slate-200 dark:border-slate-700">
        <div className="p-4">
          <div className="text-sm font-medium mb-2">Futás-napló (utolsó 30)</div>
          {(runsQ.data?.runs.length ?? 0) === 0 ? (
            <div className="text-xs text-slate-500 dark:text-slate-400 italic">
              Még nincs futás-rekord.
            </div>
          ) : (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  <tr>
                    <th className="text-left px-3 py-2">Indult</th>
                    <th className="text-left px-3 py-2">Típus</th>
                    <th className="text-left px-3 py-2">Trigger</th>
                    <th className="text-left px-3 py-2">Státusz</th>
                    <th className="text-right px-3 py-2">Idő</th>
                    <th className="text-left px-3 py-2">Eredmény</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {(runsQ.data?.runs ?? []).map((r) => (
                    <tr key={r.jobId} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="px-3 py-1.5 whitespace-nowrap font-mono">{dateTime(r.startedAt)}</td>
                      <td className="px-3 py-1.5 font-mono text-slate-600 dark:text-slate-300">
                        {r.type.replace(/^cron\./, '')}
                      </td>
                      <td className="px-3 py-1.5">
                        <span
                          className={cn(
                            'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium',
                            r.trigger === 'cron'
                              ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                              : 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
                          )}
                        >
                          {r.trigger}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        {r.status === 'success' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" /> OK
                          </span>
                        ) : r.status === 'failed' ? (
                          <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-300">
                            <XCircle className="h-3 w-3" /> fail
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
                            running
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500 dark:text-slate-400">
                        {r.durationMs != null ? `${r.durationMs} ms` : '—'}
                      </td>
                      <td className="px-3 py-1.5 max-w-md truncate text-slate-600 dark:text-slate-400">
                        {r.resultSummary ?? r.errorMessage ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// OverviewSection — integrációk és figyelmeztetések áttekintése
// ─────────────────────────────────────────────────────────────────────────────

const HEALTH_TONE: Record<string, { dot: string; chip: string; label: string }> = {
  ok: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200', label: 'OK' },
  warn: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200', label: 'figyelem' },
  error: { dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-200', label: 'hiba' },
  not_configured: { dot: 'bg-slate-400', chip: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', label: 'nincs beállítva' },
}

function OverviewSection() {
  const q = useHealthIntegrations()
  if (q.isLoading || !q.data) return <CenterSpinner />

  const { integrations, counters } = q.data

  return (
    <div className="space-y-4">
      {/* Figyelmeztető számlálók */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <CounterCard
          label="Lejárt számlák"
          value={counters.overdueCount}
          tone={counters.overdueCount > 0 ? 'amber' : 'slate'}
        />
        <CounterCard
          label="Jóváhagyásra vár"
          value={counters.pendingApprovalCount}
          tone={counters.pendingApprovalCount > 0 ? 'amber' : 'slate'}
        />
        <CounterCard
          label="Eltérés a NAV-tól"
          value={counters.discrepancyCount}
          tone={counters.discrepancyCount > 0 ? 'red' : 'slate'}
        />
      </div>

      {/* Integrációk állapota */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Integrációk állapota</CardTitle>
            <CardSubtitle>
              Egy pillantásra, hogy melyik külső kapcsolat él. 30 mp-enként frissül.
            </CardSubtitle>
          </div>
        </CardHeader>
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {integrations.map((it) => {
            const t = HEALTH_TONE[it.status] ?? HEALTH_TONE.warn
            return (
              <div key={it.key} className="p-4 flex items-start gap-3">
                <div className="mt-1.5 flex-shrink-0">
                  <span className={cn('inline-block h-2.5 w-2.5 rounded-full', t.dot)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{it.label}</span>
                    <span className={cn('text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 font-medium', t.chip)}>
                      {t.label}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{it.detail}</p>
                  {it.lastSeenAt && (
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                      Utolsó aktivitás: {dateTime(it.lastSeenAt)}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

function CounterCard({ label, value, tone }: { label: string; value: number; tone: 'amber' | 'red' | 'slate' }) {
  const toneClass =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-900/40 text-amber-900 dark:text-amber-100'
      : tone === 'red'
        ? 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-900/40 text-red-900 dark:text-red-100'
        : 'border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700'
  return (
    <div className={`rounded-lg border ${toneClass} p-4`}>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  )
}
